import { Fragment, useState, type ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Archive, ArchiveRestore, FolderInput, Pencil, RotateCcw } from 'lucide-react';
import { applyForget, archiveItem, restoreItem, revertItem, updateItem } from '@mkcore/knowledge/schema';
import { Badge, Button, Card, ErrorState, Field, Input, PageHeader, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, cn } from '@mkcore/ui';
import { ConfirmButton } from '@/components/confirm';
import { MarkdownEditor } from '@/components/markdown-editor';
import { formatDate, formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { MoveItemDialog } from '@/features/knowledge/dialogs';
import { RelatedKnowledge } from '@/features/knowledge/panels';
import { ItemEntityLink } from '@/features/knowledge/graph';
import { itemEntityQuery, itemQuery, linksQuery, provenanceQuery, typesQuery, versionQuery, versionsQuery, type Item } from '@/features/knowledge/queries';
import { DiffView, KindIcon, KnowledgeMarkdown, SpaceLabel, TypeBadge } from '@/features/knowledge/ui';

const search = z.object({ tab: z.enum(['content', 'versions']).optional(), v: z.coerce.number().int().optional() });

export const Route = createFileRoute('/_app/knowledge/items/$id')({ validateSearch: search, component: ItemPage });

const tagsOf = (s: string) => [...new Set(s.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean))].slice(0, 30);

/** One knowledge item (CO-1107, CO-0500, CO-0501, CO-0502): the text with its links, the editor, the history. */
function ItemPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const canUpdate = useCan('knowledge:update');
  const canDelete = useCan('knowledge:delete');
  const q = useQuery(itemQuery(id));
  const links = useQuery(linksQuery(id));
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const archive = useActionMutation(archiveItem, { success: t('knowledge.item.archived') });
  const restore = useActionMutation(restoreItem, { success: t('knowledge.item.restored') });
  const forget = useActionMutation(applyForget, { success: t('knowledge.item.forgotten'), onSuccess: () => void navigate({ to: '/knowledge' }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const item = q.data;
  const writable = canUpdate && item.space && !item.lockedForAgents;
  const tab = s.tab ?? 'content';
  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/knowledge" search={{ space: item.spaceId, ...(item.path && { path: item.path }) }} className="hover:underline">
            {item.space.name}
            {item.path && ` / ${item.path}`}
          </Link>
        }
        title={item.title}
        actions={
          <div className="flex flex-wrap gap-2">
            {writable && item.status === 'active' && !editing && (
              <Button variant="secondary" onClick={() => (setEditing(true), void navigate({ search: (p) => ({ ...p, tab: undefined }), replace: true }))}>
                <Pencil /> {t('common.edit')}
              </Button>
            )}
            {writable && (
              <Button variant="ghost" onClick={() => setMoving(true)}>
                <FolderInput /> {t('knowledge.item.move')}
              </Button>
            )}
            {writable &&
              (item.status === 'active' ? (
                <Button variant="ghost" loading={archive.isPending} onClick={() => archive.mutate({ input: { id } })}>
                  <Archive /> {t('knowledge.item.archive')}
                </Button>
              ) : (
                <Button variant="ghost" loading={restore.isPending} onClick={() => restore.mutate({ input: { id } })}>
                  <ArchiveRestore /> {t('knowledge.item.restore')}
                </Button>
              ))}
            {canDelete && (
              <ConfirmButton
                label={t('knowledge.item.forget')}
                title={t('knowledge.item.forgetTitle')}
                description={t('knowledge.item.forgetHint')}
                variant="danger"
                onConfirm={() => forget.mutateAsync({ input: { ids: [{ type: 'item', id }] } })}
              />
            )}
          </div>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <TypeBadge type={item.type} />
        {item.status === 'archived' && <Badge tone="warning">{t('knowledge.item.isArchived')}</Badge>}
        {item.validUntil && <Badge tone="warning">{t('knowledge.item.superseded', { date: formatDate(item.validUntil.slice(0, 10)) })}</Badge>}
        {item.lockedForAgents && <Badge tone="outline">{t('knowledge.item.locked')}</Badge>}
        {item.tags.map((tag) => (
          <Link key={tag} to="/knowledge" search={{ tag }}>
            <Badge tone="outline">#{tag}</Badge>
          </Link>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <Tabs value={tab} onValueChange={(v) => void navigate({ search: (p) => ({ ...p, tab: v === 'content' ? undefined : (v as 'versions') }), replace: true })}>
            <TabsList>
              <TabsTrigger value="content">{t('knowledge.item.content')}</TabsTrigger>
              <TabsTrigger value="versions">{t('knowledge.versions.title')}</TabsTrigger>
            </TabsList>
            <TabsContent value="content" className="pt-3">
              {editing ? (
                <Editor item={item} onDone={() => setEditing(false)} />
              ) : (
                <Card className="p-5">{item.body.trim() ? <KnowledgeMarkdown text={item.body} links={links.data?.links} spaceId={item.spaceId} /> : <p className="text-sm text-fg-3">{t('knowledge.item.empty')}</p>}</Card>
              )}
            </TabsContent>
            <TabsContent value="versions" className="pt-3">
              <Versions item={item} selected={s.v} onSelect={(v) => void navigate({ search: (p) => ({ ...p, v }), replace: true })} canRevert={!!writable} />
            </TabsContent>
          </Tabs>
        </div>
        <div className="grid content-start gap-4">
          <Details item={item} />
          <Links id={id} />
          <Provenance id={id} />
          <RelatedKnowledge type="item" id={id} />
        </div>
      </div>
      {moving && <MoveItemDialog item={item} onClose={() => setMoving(false)} />}
    </>
  );
}

/** Edit title, tags and text; the old text stays as a version (CO-0500, CO-1103). */
function Editor({ item, onDone }: { item: Item; onDone: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [title, setTitle] = useState(item.title);
  const [tags, setTags] = useState(item.tags.join(', '));
  const [body, setBody] = useState(item.body);
  const [error, setError] = useState<string | null>(null);
  const save = useActionMutation(updateItem, { silentErrors: true, success: t('knowledge.item.saved'), onSuccess: onDone });
  return (
    <Card className="grid gap-3 p-4">
      <Field label={t('knowledge.item.fields.title')}>{(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
      <Field label={t('knowledge.item.fields.tags')} hint={t('knowledge.item.tagsHint')}>
        {(p) => <Input {...p} value={tags} onChange={(e) => setTags(e.target.value)} />}
      </Field>
      <MarkdownEditor aria-label={t('knowledge.item.fields.body')} rows={20} value={body} onChange={setBody} />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          disabled={!title.trim()}
          loading={save.isPending}
          onClick={() => save.mutate({ input: { id: item.id, title: title.trim(), body, tags: tagsOf(tags) }, ifMatch: item.version }, { onError: (e) => setError(message(e)) })}
        >
          {t('common.save')}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </Card>
  );
}

/** The history with a diff per version, and going back to one (CO-0501). */
function Versions({ item, selected, onSelect, canRevert }: { item: Item; selected?: number; onSelect: (v: number) => void; canRevert: boolean }) {
  const { t } = useTranslation();
  const versions = useQuery(versionsQuery(item.id));
  const rows = versions.data?.items ?? [];
  const current = selected ?? rows[0]?.number;
  const version = useQuery({ ...versionQuery(item.id, current ?? 1), enabled: !!current });
  const revert = useActionMutation(revertItem, { success: t('knowledge.versions.reverted') });
  if (versions.isLoading) return <Skeleton className="h-48" />;
  return (
    <div className="grid gap-4 md:grid-cols-[14rem_minmax(0,1fr)]">
      <ol className="grid content-start gap-1" aria-label={t('knowledge.versions.title')}>
        {rows.map((v) => (
          <li key={v.number}>
            <button type="button" aria-current={v.number === current ? 'true' : undefined} className={cn('grid w-full rounded px-2 py-1 text-left text-sm hover:bg-surface-alt', v.number === current && 'bg-surface-alt')} onClick={() => onSelect(v.number)}>
              <span className="font-bold">
                {t('knowledge.versions.version', { n: v.number })} · {t(`knowledge.versions.reasons.${v.reason}`)}
              </span>
              <span className="text-xs text-fg-3">
                {v.authorName || t(`knowledge.authors.${v.authorKind}`)} · {formatDateTime(v.createdAt)}
              </span>
            </button>
          </li>
        ))}
      </ol>
      <div className="grid content-start gap-2">
        {version.data && (
          <>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-fg-2">{t('knowledge.versions.diffAgainst', { n: version.data.number })}</p>
              {canRevert && version.data.number !== item.version && (
                <ConfirmButton
                  label={t('knowledge.versions.revert')}
                  icon={<RotateCcw />}
                  title={t('knowledge.versions.revertTitle', { n: version.data.number })}
                  description={t('knowledge.versions.revertHint')}
                  variant="secondary"
                  onConfirm={() => revert.mutateAsync({ input: { id: item.id, number: version.data!.number }, ifMatch: item.version })}
                />
              )}
            </div>
            <DiffView diff={version.data.diff} />
          </>
        )}
      </div>
    </div>
  );
}

function Details({ item }: { item: Item }) {
  const { t } = useTranslation();
  const entity = useQuery(itemEntityQuery(item.id)).data;
  const spec = useQuery(typesQuery()).data?.itemTypes.find((x) => x.type === item.type);
  const known = new Set(spec?.fields.map((f) => f.name) ?? []);
  const fields = Object.entries(item.fields).filter(([, v]) => v !== null && v !== undefined && v !== '');
  const show = (v: unknown) => (Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v));
  const row = (label: string, value: ReactNode) =>
    value ? (
      <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2">
        <dt className="text-fg-3">{label}</dt>
        <dd className="min-w-0 break-words">{value}</dd>
      </div>
    ) : null;
  return (
    <Card role="region" aria-label={t('knowledge.item.details')} className="p-4">
      <dl className="grid gap-1.5 text-sm">
        {row(t('knowledge.item.fields.space'), <SpaceLabel space={item.space} />)}
        {row(t('knowledge.item.fields.path'), item.path)}
        {row(t('knowledge.item.fields.eventDate'), item.eventDate && formatDate(item.eventDate))}
        {row(t('knowledge.item.fields.sources'), !!item.sources.length && <ul className="grid gap-0.5">{item.sources.map((x) => <li key={x}>{/^https?:\/\//.test(x) ? <a href={x} target="_blank" rel="noopener noreferrer" className="underline">{x}</a> : x}</li>)}</ul>)}
        {row(t('knowledge.item.fields.author'), t(`knowledge.authors.${item.createdByKind}`))}
        {row(t('knowledge.item.fields.updated'), formatDateTime(item.updatedAt))}
        {row(t('knowledge.item.fields.origin'), item.origin)}
        {fields.map(([k, v]) => <Fragment key={k}>{row(known.has(k) ? t(`knowledge.fieldNames.${k}`, { defaultValue: k }) : k, show(v))}</Fragment>)}
        {entity && row(t('knowledge.graph.inGraph'), <ItemEntityLink entity={entity} />)}
        {item.supersededBy && row(t('knowledge.item.fields.supersededBy'), <Link to="/knowledge/items/$id" params={{ id: item.supersededBy }} className="underline">{t('knowledge.item.newer')}</Link>)}
      </dl>
    </Card>
  );
}

/** Links of the item and the items that link to it (CO-0502). */
function Links({ id }: { id: string }) {
  const { t } = useTranslation();
  const q = useQuery(linksQuery(id));
  const links = q.data?.links ?? [];
  const back = q.data?.backlinks ?? [];
  if (!links.length && !back.length) return null;
  return (
    <Card role="region" aria-label={t('knowledge.links.title')} className="grid gap-3 p-4">
      {!!links.length && (
        <div className="grid gap-1">
          <h2 className="text-sm font-bold">{t('knowledge.links.out')}</h2>
          <ul className="grid gap-1 text-sm">
            {links.map((l) => (
              <li key={l.target} className="flex items-center gap-1.5">
                {l.kind === 'item' && l.exists && l.id ? (
                  <Link to="/knowledge/items/$id" params={{ id: l.id }} className="hover:underline">
                    {l.label ?? l.title}
                  </Link>
                ) : l.url ? (
                  <a href={l.url} className="hover:underline" {...(l.kind === 'issue' && { target: '_blank', rel: 'noopener noreferrer' })}>
                    {l.label ?? l.title ?? l.target}
                  </a>
                ) : (
                  <span className="text-fg-3">{l.label ?? l.title ?? l.target}</span>
                )}
                {l.status && <Badge tone={l.status === 'open' ? 'info' : 'outline'}>{l.status}</Badge>}
                {!l.exists && <Badge tone="outline">{t(l.kind === 'item' ? 'knowledge.links.missing' : 'knowledge.links.unknown')}</Badge>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!!back.length && (
        <div className="grid gap-1">
          <h2 className="text-sm font-bold">{t('knowledge.links.back')}</h2>
          <ul className="grid gap-1 text-sm">
            {back.map((b) => (
              <li key={b.id} className="flex items-center gap-1.5">
                <KindIcon kind="item" />
                <Link to="/knowledge/items/$id" params={{ id: b.id }} className="hover:underline">
                  {b.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** Where the item comes from and what was made of it (CO-1093). */
function Provenance({ id }: { id: string }) {
  const { t } = useTranslation();
  const q = useQuery(provenanceQuery('item', id));
  const p = q.data;
  if (!p || (!p.sources.length && !p.derived.length)) return null;
  return (
    <Card role="region" aria-label={t('knowledge.provenance.title')} className="grid gap-2 p-4">
      <h2 className="text-sm font-bold">{t('knowledge.provenance.title')}</h2>
      {!!p.sources.length && (
        <ul className="grid gap-1 text-sm">
          {p.sources.map((x) => (
            <li key={`${x.type}:${x.id}`} className="flex items-center gap-1.5">
              <span className="text-fg-3">{t('knowledge.provenance.from')}</span>
              {x.title && (x.type === 'item' || x.type === 'document') ? (
                <Link to={x.type === 'item' ? '/knowledge/items/$id' : '/knowledge/documents/$id'} params={{ id: x.id }} className="hover:underline">
                  {x.title}
                </Link>
              ) : (
                <span className="text-fg-3">{t('knowledge.provenance.hidden')}</span>
              )}
              {x.run && <Badge tone="outline">{t(`knowledge.provenance.run.${x.run.kind}`)}</Badge>}
            </li>
          ))}
        </ul>
      )}
      {!!p.derived.length && (
        <ul className="grid gap-1 text-sm">
          {p.derived.map((x) => (
            <li key={`${x.type}:${x.id}`} className="flex items-center gap-1.5">
              <span className="text-fg-3">{t('knowledge.provenance.made')}</span>
              <Badge tone="outline">{t(`knowledge.provenance.types.${x.type}`)}</Badge>
              {x.type === 'item' ? (
                <Link to="/knowledge/items/$id" params={{ id: x.id }} className="hover:underline">
                  {x.title ?? x.id}
                </Link>
              ) : (
                <span>{x.title ?? t(`knowledge.provenance.types.${x.type}`)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
